import { useState } from 'react';
import {
  IAdmissionDATA,
  IAdmissionResponse,
  ICompanyAssociation,
  ICompanyAssociationResponse,
  ICompanyBusinessLicenseResponse,
  ICompanyCivilAviation,
  ICompanyCivilAviationResponse,
  ICompanyDetails,
  ICompanyDocTradeCivil,
  ICompanyDocumentResponse,
  ICompanyLicense,
  ICompanyLicenseResponse,
  ICompanyOwnerList,
  ICompanyProposer,
  ICompanyProposerResponse,
  ICompanyRepresentativeDetails,
  ICompanyRepresentativeDetailsResponse,
  IViewCompanyBusinessLicenseInfo,
  IResponse,
} from './types';
import {
  fetchAdmissionData,
  fetchCompanyAssociation,
  fetchCompanyBusinessLicense,
  fetchCompanyCivilAviation,
  fetchCompanyDetails,
  fetchCompanyDocument,
  fetchCompanyLicense,
  fetchCompanyOwner,
  fetchCompanyProposer,
  fetchOwnerDetails,
  postCompanyAssociation,
  postCompanyBusinessLicense,
  postCompanyCivilAviation,
  postCompanyDocTradeCivil,
  postCompanyLicense,
  postCompanyProposer,
} from '.';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { API_ENDPOINTS } from './utils/api-endpoints';
import { showToast } from '../Toaster/Toaster';
import { addOwner } from '../ReactQuery/Mutations/CompanyOwnerAddMutation';
import { HttpClient } from './utils/http';

export function useCompanyDetailsQuery() {
  const { data, isLoading, error } = useQuery(
    [API_ENDPOINTS.COMPANY_DETAILS],
    () => fetchCompanyDetails(),
    {
      refetchOnWindowFocus: false,
    }
  );

  return {
    companyData: data?.data ?? ({} as ICompanyDetails),
    isLoading,
    error,
  };
}
export function useOwnerDetailsQuery(id: string) {
  const { data, isLoading, error } = useQuery<
    ICompanyRepresentativeDetailsResponse,
    Error
  >(
    [API_ENDPOINTS.COMPANY_REPRESENTATIVE, { id }],
    () => fetchOwnerDetails(id as string),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyRepresentativeInfo:
      data?.data ?? ({} as ICompanyRepresentativeDetails),
    isLoading,
    error,
  };
}
export function useCompanyOwnerQuery() {
  const { data, isLoading, error } = useQuery<any, Error>(
    [API_ENDPOINTS.OWNER_DETAILS],
    () => fetchCompanyOwner(),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyOwnerData: data?.data ?? ([] as ICompanyOwnerList[]),
    isLoading,
    error,
  };
}

export function useCompanyLicense() {
  const { data, isLoading, error } = useQuery<ICompanyLicenseResponse, Error>(
    [API_ENDPOINTS.OWNER_LICENSE],
    () => fetchCompanyLicense(),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyLicense: data?.data ?? ({} as ICompanyLicense),
    isLoading,
    error,
  };
}
export function useCompanyLicenseUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(postCompanyLicense, {
    // onSuccess: (data) => {
    //   if (data?.success) {
    //     showToast(`${data.message}`, "success");
    //   }
    // },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};
      showToast(`${data.message}`, 'error');
      setFormError(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.OWNER_LICENSE],
      });
    },
  });

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

export function useCompanyBusinessLicense() {
  const { data, isLoading, error } = useQuery<
    ICompanyBusinessLicenseResponse,
    Error
  >(
    [API_ENDPOINTS.OWNER_BUSINESS_LICENSE],
    () => fetchCompanyBusinessLicense(),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyBusinessLicense:
      data?.data ?? ({} as IViewCompanyBusinessLicenseInfo),
    isLoading,
    error,
  };
}

export function useCompanyBusinessLicenseUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(
    postCompanyBusinessLicense,
    {
      // onSuccess: (data) => {
      //   if (data?.success) {
      //     showToast(`${data.message}`, "success");
      //   }
      // },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};
        showToast(`${data.message}`, 'error');
        setFormError(data?.message);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.OWNER_BUSINESS_LICENSE],
        });
      },
    }
  );

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

export function useCompanyAssociation() {
  const { data, isLoading, error } = useQuery<
    ICompanyAssociationResponse,
    Error
  >([API_ENDPOINTS.COMPANY_ASSOCIATION], () => fetchCompanyAssociation(), {
    refetchOnWindowFocus: false,
  });
  return {
    companyAssociation: data?.data ?? ({} as ICompanyAssociation),
    isLoading,
    error,
  };
}

export function useCompanyAssociationUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(postCompanyAssociation, {
    // onSuccess: (data) => {
    //   if (data?.success) {
    //     showToast(`${data.message}`, "success");
    //   }
    // },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};
      showToast(`${data.message}`, 'error');
      setFormError(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.COMPANY_ASSOCIATION],
      });
    },
  });

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

export function useCompanyCivilAviation() {
  const { data, isLoading, error } = useQuery<
    ICompanyCivilAviationResponse,
    Error
  >([API_ENDPOINTS.COMPANY_CIVIL_AVIATION], () => fetchCompanyCivilAviation(), {
    refetchOnWindowFocus: false,
  });
  return {
    companyCivilAviation: data?.data ?? ({} as ICompanyCivilAviation),
    isLoading,
    error,
  };
}

export function useCompanyCivilAviationUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(
    postCompanyCivilAviation,
    {
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};
        showToast(`${data?.message}`, 'error');
        setFormError(data?.message);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.COMPANY_CIVIL_AVIATION],
        });
      },
    }
  );

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

export function useCompanyProposer(id: string) {
  const { data, isLoading, error } = useQuery<ICompanyProposerResponse, Error>(
    [API_ENDPOINTS.COMPANY_PROPOSER_UPDATE],
    () => fetchCompanyProposer(id as string),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyProposer: data?.data ?? ([] as ICompanyProposer[]),
    isLoading,
    error,
  };
}
export function useCompanyProposerUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(postCompanyProposer, {
    // onSuccess: (data) => {
    //   if (data?.success) {
    //     showToast(`${data.message}`, "success");
    //   }
    // },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};
      showToast(`${data.message}`, 'error');
      setFormError(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.COMPANY_PROPOSER_UPDATE],
      });
    },
  });

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

export function useCompanyDocument() {
  const { data, isLoading, error } = useQuery<ICompanyDocumentResponse, Error>(
    [API_ENDPOINTS.COMPANY_DOC_LICENSE_CIVILAVIATION],
    () => fetchCompanyDocument(),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    companyDocument: data?.data ?? ({} as ICompanyDocTradeCivil),
    isLoading,
    error,
  };
}
export const updateCompanyDocTradeAndCivilMutation = async ({
  data,
}: {
  data: any;
}) => {
  return await HttpClient.patch(
    `${API_ENDPOINTS.COMPANY_DOC_LICENSE_CIVILAVIATION}`,
    data
  );
};
export function useCompanyDocumentTradeAndCivilUpdate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(
    updateCompanyDocTradeAndCivilMutation,
    {
      onSuccess: (data) => {
        if (data) {
          showToast(`Successfully updated company document`, 'success');
        }
      },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};
        showToast(`${data.message}`, 'error');
        setFormError(data?.message);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.COMPANY_DOC_LICENSE_CIVILAVIATION],
        });
      },
    }
  );

  return { mutate, isLoading, formError, setFormError, isSuccess };
}
export function useCompanyDocumentTradeAndCivilCreate() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(
    postCompanyDocTradeCivil,
    {
      // onSuccess: (data) => {
      //   if (data?.success) {
      //     showToast(`Successfully updated company document`, "success");
      //   }
      // },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};
        showToast(`${data.message}`, 'error');
        setFormError(data?.message);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.COMPANY_DOC_LICENSE_CIVILAVIATION],
        });
      },
    }
  );

  return { mutate, isLoading, formError, setFormError, isSuccess };
}
/**
 * Fetches admission data for a given ID.
 *
 * @param id The ID of the admission record.
 * @returns An object containing the admission data, loading state, and error state.
 */
export function useAdmission(id: string) {
  const { data, isLoading, error } = useQuery<IAdmissionResponse, Error>(
    [API_ENDPOINTS.ADMISSION, addOwner, { id }],
    () => fetchAdmissionData(id as string)
  );
  return {
    admissionData: data?.data ?? ({} as IAdmissionDATA),
    isLoading,
    error,
  };
}

export interface IAdditionalDocument {
  id: number;
  file: string;
  created_at: string;
  updated_at: string;
}

export interface IAdditionalDocumentsResponse extends IResponse {
  data?: IAdditionalDocument[];
}

export const fetchAdditionalDocuments = async () => {
  return await HttpClient.get<IAdditionalDocumentsResponse>(
    '/api/atab/member-company/member/additional-documents'
  );
};

export const postAdditionalDocuments = async (formData: FormData) => {
  return await HttpClient.post<IResponse>(
    '/api/atab/member-company/member/additional-documents',
    formData
  );
};

export function useAdditionalDocumentsQuery() {
  const { data, isLoading, error, refetch } = useQuery<
    IAdditionalDocumentsResponse,
    Error
  >(
    ['additional-documents'],
    () => fetchAdditionalDocuments(),
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    additionalDocuments: data?.data ?? [],
    isLoading,
    error,
    refetch,
  };
}

export function useAdditionalDocumentsUpload() {
  const [formError, setFormError] = useState(null);
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useMutation(
    postAdditionalDocuments,
    {
      onSuccess: (data) => {
        if (data) {
          showToast(`Successfully uploaded additional document(s)`, 'success');
        }
      },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};
        showToast(`${data?.message || 'Failed to upload document(s)'}`, 'error');
        setFormError(data?.message);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ['additional-documents'],
        });
      },
    }
  );

  return { mutate, isLoading, formError, setFormError, isSuccess };
}

